import { gql } from '@apollo/client';

/**
 * Operational clauses on client contracts (sub-processor changes, breach
 * notification). Types are hand-written like contracts.operations.ts.
 */

export type SubprocessorConsent = 'NOTICE' | 'OBJECTION' | 'PRIOR_CONSENT';
export type ContractClauseKind = 'SUBPROCESSOR_CHANGE' | 'BREACH_NOTIFICATION';
export type ContractClauseStanding = 'STATED' | 'NOT_STATED' | 'NOT_RECORDED';

export interface SubprocessorChangeClause {
  consent: SubprocessorConsent;
  noticeDays?: number | null;
  objectionDays?: number | null;
  notes?: string | null;
  quote?: string | null;
}

export interface BreachNotificationClause {
  hours?: number | null;
  notes?: string | null;
  quote?: string | null;
}

export interface ContractClauses {
  subprocessorChange?: SubprocessorChangeClause | null;
  breachNotification?: BreachNotificationClause | null;
  source?: 'MANUAL' | 'DOCUMENT';
  updatedAt?: string;
}

/** What reading a document found: a starting point a person checks. */
export interface ContractClauseProposal {
  title?: string | null;
  contractType?: string | null;
  signedAt?: string | null;
  subprocessorChange?:
    | (Omit<SubprocessorChangeClause, 'consent'> & {
        consent?: SubprocessorConsent | null;
      })
    | null;
  breachNotification?: BreachNotificationClause | null;
  existingContractId?: string | null;
}

export interface ContractClauseRegisterRow {
  organizationId: string;
  organizationName: string;
  standing: ContractClauseStanding;
  contractId?: string | null;
  contractName?: string | null;
  signedAt?: string | null;
  clauses?: ContractClauses | null;
  dpoContacts: Array<{ id: string; name: string; email?: string | null }>;
}

const CLAUSE_FIELDS = `
  subprocessorChange {
    consent
    noticeDays
    objectionDays
    notes
    quote
  }
  breachNotification {
    hours
    notes
    quote
  }
`;

/** Whether the caller is a workspace admin or owner: who manages contract templates and clauses. */
export const CAN_MANAGE_CONTRACTS = gql`
  query CanManageContracts {
    canManageContracts
  }
`;

export const CONTRACT_CLAUSE_REGISTER = gql`
  query ContractClauseRegister($kind: ContractClauseKind!) {
    contractClauseRegister(kind: $kind) {
      organizationId
      organizationName
      standing
      contractId
      contractName
      signedAt
      clauses {
        ${CLAUSE_FIELDS}
        source
        updatedAt
      }
      dpoContacts {
        id
        name
        email
      }
    }
  }
`;

export const READ_CONTRACT_CLAUSES = gql`
  mutation ReadContractClauses($documentId: ID!) {
    readContractClauses(documentId: $documentId) {
      title
      contractType
      signedAt
      existingContractId
      ${CLAUSE_FIELDS}
    }
  }
`;

const CONTRACT_WITH_CLAUSES = `
  id
  name
  clauses {
    ${CLAUSE_FIELDS}
    source
    updatedAt
  }
`;

export const RECORD_CONTRACT_FROM_DOCUMENT = gql`
  mutation RecordContractFromDocument($input: RecordContractFromDocumentInput!) {
    recordContractFromDocument(input: $input) {
      ${CONTRACT_WITH_CLAUSES}
    }
  }
`;

export const UPDATE_CONTRACT_CLAUSES = gql`
  mutation UpdateContractClauses($contractId: ID!, $clauses: ContractClausesInput!) {
    updateContractClauses(contractId: $contractId, clauses: $clauses) {
      ${CONTRACT_WITH_CLAUSES}
    }
  }
`;
